import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Badge, Button, Icon, Table, Tooltip } from '@syneto/compass-react';
import Preloader from '../../../../components/preloader';
import { handleSort } from '../../../../helpers/TableUtilities';
import MaintenanceStatusFormatter from '../../../../helpers/MaintenanceStatusFormatter';
import { DEFAULT_LICENSE_STATUS_FILTER, LICENSE_STATUS_FILTERS, filterLicensesByStatus, isProtectedLicense } from './licenseRules';

export const PROTECTED_LICENSE_HINT = 'Licenses for machines on 6.1 or later and the licenses Central ships with cannot be deleted.';

const processTableData = (addons) => {
	return addons.map((addon) => {
		return {
			name: addon.name ?? '',
			id: addon.id ?? '',
			summary: addon.summary ?? '',
			trialDuration: addon.trialDuration ?? '',
			featurePeriod: addon.featurePeriod ?? '',
			supportedProductVersions: addon.supportedProductVersions ?? [],
			status: addon.status,
			code: addon.code ?? '',
			syncScope: addon.syncScope ?? null
		};
	});
};

const OverflowTip = ({ children }) => {
	const [isOverflowed, setIsOverflow] = useState(false);
	const textElementRef = useRef();
	useEffect(() => {
		setIsOverflow(textElementRef.current.scrollWidth > textElementRef.current.clientWidth);
	}, []);
	return (
		<Tooltip text={children} placement="top" trigger={isOverflowed ? 'hover' : ''}>
			<div
				ref={textElementRef}
				style={{
					whiteSpace: 'nowrap',
					overflow: 'hidden',
					textOverflow: 'ellipsis',
				}}
			>
				{children}
			</div>
		</Tooltip>
	);
};

export const ProductAddonsTable = (props) => {
	const { loading, addons, setEditedAddon, setItemToRemove } = props;
	const [statusFilter, setStatusFilter] = useState(DEFAULT_LICENSE_STATUS_FILTER);

	const handleTableChange = (type, { page, sizePerPage, searchText, sortOrder, sortField, data }) => {
		switch (type) {
			case 'sort': {
				handleSort(sortOrder, sortField, data);
				return;
			}
		}
	};

	const openEditModal = (selectedAddonId) => {
		const selectedAddon = addons.find((addon) => { return addon.id === selectedAddonId; });

		if (selectedAddon) {
			setEditedAddon(selectedAddon);
		}
	};

	const renderSummary = (cell, row) => {
		return (
			<>
				<OverflowTip>{row.summary}</OverflowTip>
			</>
		);
	};

	const renderSupportedVersions = (cell, row) => {
		return (
			<>
				<OverflowTip>{formatSupportedVersions(row.supportedProductVersions)}</OverflowTip>
			</>
		);
	};

	const formatSupportedVersions = (versions) => {
		return versions.map((version, index) => {
			return index ? ', ' + version : version;
		});
	};

	const renderStatus = (cell, row) => {
		return (
			<>
				<Badge appearance={MaintenanceStatusFormatter.setStyleAddon(row.status)}>
					{row.status}
				</Badge>
			</>
		);
	};

	const renderActions = (cell, row) => {
		return (
			<>
				<Button role="tertiary" onClick={() => { openEditModal(row.id); }}>
					Edit
				</Button>
				{isProtectedLicense(row) ? (
					<Tooltip text={PROTECTED_LICENSE_HINT} placement="top" trigger="hover">
						<span className="d-inline-block">
							<Button role="tertiary" disabled>
								Delete
							</Button>
						</span>
					</Tooltip>
				) : (
					<Button role="tertiary" onClick={() => { setItemToRemove(new Array(row)); }}>
						Delete
					</Button>
				)}
			</>
		);
	};

	const sortPeriods = (a, b, order) => {
		const extractNumber = (str) => {
			let match = str.match(/^\d+/);
			return match ? parseInt(match[0], 10) : 0;
		};
		a = extractNumber(a);
		b = extractNumber(b);
		return (order === 'desc' ? b - a : a - b);
	};

	const renderStatusFilter = () => {
		return (
			<div className="d-flex align-items-center">
				<label htmlFor="license-status-filter" className="me-2 text-nowrap">Status</label>
				<select
					id="license-status-filter"
					className="form-select w-auto"
					value={statusFilter}
					onChange={(event) => { setStatusFilter(event.target.value); }}
				>
					{LICENSE_STATUS_FILTERS.map((filter) => {
						return (
							<option key={filter.value} value={filter.value}>
								{filter.label} ({filterLicensesByStatus(addons, filter.value).length})
							</option>
						);
					})}
				</select>
			</div>
		);
	};

	const emptyTableState = () => {
		return loading
			? <Preloader dataIsLoading={true} />
			: 'No product licenses found';
	};

	return (
		<div className="p-4 bg-white">
			<Table
				data={processTableData(filterLicensesByStatus(addons, statusFilter))}
				keyField="id"
				selectable="multiple"
				// search={{ defaultSearch: queryParams.searchFor }}
				actions={[{
					label: 'Delete selected',
					onClick: (selected) => { return setItemToRemove(selected); }
				}]}
				extra={renderStatusFilter()}
				onTableChange={handleTableChange}
				noDataIndication={emptyTableState()}
			>
				<Table.Col field="name" align="left" sort>Name</Table.Col>
				<Table.Col field="summary" align="left" sort renderCell={renderSummary}>Summary</Table.Col>
				<Table.Col field="trialDuration" align="left" sort={sortPeriods}>Trial duration</Table.Col>
				<Table.Col field="featurePeriod" align="left" sort={sortPeriods}>Feature period</Table.Col>
				<Table.Col field="status" align="left" sort renderCell={renderSupportedVersions}>Versions</Table.Col>
				<Table.Col field="status" align="left" sort renderCell={renderStatus}>Status</Table.Col>
				<Table.Col align="center" renderCell={renderActions}>Actions</Table.Col>
			</Table>
		</div>
	);
};

ProductAddonsTable.propTypes = {
	addons: PropTypes.array.isRequired,
	loading: PropTypes.bool.isRequired
};
